import { Client } from '@microsoft/microsoft-graph-client';
import { OpportunityItem, UpdateTechnicalReviewPayload } from '../models/opportunity';
import { NovaOportunidadeForm } from '../components/NovaOportunidadeModal';

export class GraphService {
  private client: Client;
  private siteId: string;
  private listId: string;
  private powerAutomateUrl: string;

  constructor(client: Client, siteId: string, listId: string, powerAutomateUrl?: string) {
    this.client = client;
    this.siteId = siteId;
    this.listId = listId;
    this.powerAutomateUrl = powerAutomateUrl || '';
  }

  private migrateStatus(raw: string): string {
    const map: Record<string, string> = {
      '1. Levantamento Inicial':         '1. Levantamento Inicial',
      '2. Viabilidade Técnica Pendente': '2. Levantamento de Necessidades',
      '3. Proposta em Elaboração':       '4. Proposta em Elaboração',
      '4. Proposta Enviada':             '5. Proposta Enviada',
      '5. Ganho / Aceite':               '10. Concluído / Ganho',
      '6. Perdido / Cancelado':          '11. Perdido / Cancelado',
    };
    return map[raw] || raw;
  }

  async getOpportunities(): Promise<OpportunityItem[]> {
    const response = await this.client
      .api(`/sites/${this.siteId}/lists/${this.listId}/items`)
      .expand('fields')
      .get();

    return response.value.map((item: any) => {
      const f = item.fields;
      const rawStatus = f.StatusOportunidade || '1. Levantamento Inicial';
      const status = this.migrateStatus(rawStatus);
      const execStatuses = ['8. Em Execução', '9. Entrega & Validação', '10. Concluído / Ganho'];
      const progresso = execStatuses.includes(status) && f.ProgressoExecucao != null
        ? Number(f.ProgressoExecucao)
        : undefined;
      return {
        id: item.id,
        codigoOportunidade: f.Title || '',
        cliente: f.Cliente || '',
        contactoCliente: f.ContactoCliente || '',
        comercialEmail: f.ComercialEmail || '',
        comercialNome: f.ComercialNome || 'Comercial',
        statusOportunidade: status,
        ambitoNecessidades: f.AmbitoNecessidades || '',
        responsavelTecnicoEmail: f.TecnicoEmail || '',
        responsavelTecnicoNome: f.TecnicoNome || 'Técnico Não Atribuído',
        statusTecnico: f.StatusTecnico || 'Não Iniciado',
        parecerTecnico: f.ParecerTecnico || '',
        historicoParecer: f.HistoricoParecer || '',
        dataLimiteParecer: f.DataLimiteParecer ? f.DataLimiteParecer.substring(0, 10) : undefined,
        dataAceitacao: f.DataAceitacao ? f.DataAceitacao.substring(0, 10) : undefined,
        numeroOT: f.NumeroOT || '',
        progressoExecucao: progresso,
        diarioBordo: f.DiarioBordo || '',
        termoAceite: f.TermoAceite || '',
        motivoPerda: f.MotivoPerda || '',
        valorEstimado: f.ValorEstimado ? Number(f.ValorEstimado) : undefined,
        dataUltimaActualizacao: f.Modified || undefined,
        faseHistorico: f.FaseHistorico || '',
      } as OpportunityItem;
    });
  }

  async getNextOpportunityCode(): Promise<string> {
    const year = new Date().getFullYear();
    const response = await this.client
      .api(`/sites/${this.siteId}/lists/${this.listId}/items`)
      .expand('fields($select=Title)')
      .get();
    const items: any[] = response.value || [];
    const prefix = `OPP-${year}-`;
    let max = 0;
    items.forEach((item: any) => {
      const title: string = item.fields?.Title || '';
      if (title.startsWith(prefix)) {
        const num = parseInt(title.replace(prefix, ''), 10);
        if (!isNaN(num) && num > max) max = num;
      }
    });
    return `${prefix}${String(max + 1).padStart(3, '0')}`;
  }

  async getTeamMembers(): Promise<{ nome: string; email: string }[]> {
    const GROUP_ID = '050ad99b-0592-40db-92a9-6cd66b0643ea';
    const response = await this.client
      .api(`/groups/${GROUP_ID}/members`)
      .select('displayName,mail,userPrincipalName')
      .get();
    return (response.value || [])
      .map((m: any) => ({
        nome: m.displayName || '',
        email: m.mail || m.userPrincipalName || ''
      }))
      .filter((m: any) => m.email)
      .sort((a: any, b: any) => a.nome.localeCompare(b.nome));
  }

  async createOpportunity(form: NovaOportunidadeForm, teamsMessageId?: string): Promise<string> {
    const response = await this.client
      .api(`/sites/${this.siteId}/lists/${this.listId}/items`)
      .post({
        fields: {
          Title: form.titulo,
          Cliente: form.cliente,
          ContactoCliente: form.contactoCliente,
          ComercialEmail: form.comercialEmail,
          ComercialNome: form.comercialNome,
          StatusOportunidade: form.statusOportunidade,
          AmbitoNecessidades: form.ambitoNecessidades,
          TecnicoEmail: form.tecnicoEmail,
          TecnicoNome: form.tecnicoNome,
          StatusTecnico: form.statusTecnico,
          DataLimiteParecer: form.dataLimiteParecer,
          ...(form.valorEstimado ? { ValorEstimado: form.valorEstimado } : {}),
          ...(teamsMessageId ? { TeamsMessageId: teamsMessageId } : {}),
        },
      });
    return response?.id as string;
  }

  async saveTeamsMessageId(itemId: string, messageId: string): Promise<void> {
    await this.client
      .api(`/sites/${this.siteId}/lists/${this.listId}/items/${itemId}/fields`)
      .update({ TeamsMessageId: messageId });
  }

  async getTeamsMessageId(itemId: string): Promise<string | null> {
    try {
      const res = await this.client
        .api(`/sites/${this.siteId}/lists/${this.listId}/items/${itemId}/fields`)
        .select('TeamsMessageId')
        .get();
      return res?.TeamsMessageId || null;
    } catch {
      return null;
    }
  }

  async updateOpportunityStatus(
    itemId: string,
    newStatus: string,
    extraFields?: Record<string, any>,
    faseHistoricoActual?: string,
    autor?: string
  ): Promise<void> {
    // Append to FaseHistorico for audit trail
    let novoFaseHistorico = faseHistoricoActual || '[]';
    try {
      const arr = JSON.parse(novoFaseHistorico);
      arr.unshift({
        fase: newStatus,
        data: new Date().toLocaleString('pt-PT'),
        autor: autor || 'Sistema',
      });
      novoFaseHistorico = JSON.stringify(arr.slice(0, 30)); // keep last 30
    } catch { /* ignore parse errors */ }

    await this.client
      .api(`/sites/${this.siteId}/lists/${this.listId}/items/${itemId}/fields`)
      .update({
        StatusOportunidade: newStatus,
        FaseHistorico: novoFaseHistorico,
        ...extraFields,
      });
  }

  async updateTechnicalReview(
    itemId: string,
    payload: UpdateTechnicalReviewPayload,
    tecnicoNome: string,
    historicoActual: string,
    novoStatusOpp?: string,
    faseHistoricoActual?: string
  ): Promise<void> {
    const now = new Date();
    const dataHora = `${now.toLocaleDateString('pt-PT')} ${now.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}`;
    const novaEntrada = `[${dataHora}] ${tecnicoNome}:\n${payload.parecerTecnico}`;
    const novoHistorico = historicoActual
      ? `${novaEntrada}\n\n---\n\n${historicoActual}`
      : novaEntrada;

    const fieldsToUpdate: Record<string, any> = {
      StatusTecnico: payload.statusTecnico,
      ParecerTecnico: payload.parecerTecnico,
      HistoricoParecer: novoHistorico,
    };

    const finalStatus = payload.novoStatusOportunidade || novoStatusOpp;
    if (finalStatus) {
      fieldsToUpdate.StatusOportunidade = finalStatus;
      // Audit trail
      let novoFaseHistorico = faseHistoricoActual || '[]';
      try {
        const arr = JSON.parse(novoFaseHistorico);
        arr.unshift({ fase: finalStatus, data: dataHora, autor: tecnicoNome });
        novoFaseHistorico = JSON.stringify(arr.slice(0, 30));
      } catch { /* ignore */ }
      fieldsToUpdate.FaseHistorico = novoFaseHistorico;
    }

    await this.client
      .api(`/sites/${this.siteId}/lists/${this.listId}/items/${itemId}/fields`)
      .update(fieldsToUpdate);
  }

  async updateExecution(itemId: string, progresso: number, entrada: string, diarioActual: string): Promise<void> {
    const now = new Date();
    const ts = `[${now.toLocaleDateString('pt-PT')} ${now.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}]`;
    const novoRegisto = `${ts}\n${entrada}`;
    const novoDiario = diarioActual ? `${novoRegisto}\n\n---\n\n${diarioActual}` : novoRegisto;
    await this.client
      .api(`/sites/${this.siteId}/lists/${this.listId}/items/${itemId}/fields`)
      .update({ ProgressoExecucao: progresso, DiarioBordo: novoDiario });
  }

  // Export opportunities to CSV string
  exportToCsv(opps: OpportunityItem[]): string {
    const headers = [
      'Código', 'Cliente', 'Fase', 'Comercial', 'Técnico',
      'Valor Estimado (USD)', 'Data Limite', 'Progresso (%)', 'Âmbito'
    ];
    const rows = opps.map(o => [
      o.codigoOportunidade,
      o.cliente,
      o.statusOportunidade,
      o.comercialNome,
      o.responsavelTecnicoNome || '',
      o.valorEstimado != null ? String(o.valorEstimado) : '',
      o.dataLimiteParecer || '',
      o.progressoExecucao != null ? String(o.progressoExecucao) : '',
      `"${(o.ambitoNecessidades || '').replace(/"/g, '""')}"`,
    ]);
    return [headers.join(';'), ...rows.map(r => r.join(';'))].join('\r\n');
  }
}
